import { Controller } from "@hotwired/stimulus"

// All-challenges mode tab-bar. The tabs live OUTSIDE the challenges_index
// turbo-frame so a mode switch never re-renders (or flashes) them; this
// controller mirrors the active state client-side and spins the clicked tab for
// instant feedback while its panel loads. The panel's dim/crossfade is pure CSS
// (#challenges_index[busy] .ch-panel + @starting-style) — this only manages the
// persistent tab-bar. Mirrors creator_matrix_controller.
const FRAME_ID = "challenges_index"
const DEFAULT_KEY = "text-to-image"

export default class extends Controller {
  static targets = ["tab"]

  declare readonly tabTargets: HTMLElement[]

  private loadingTab: HTMLElement | null = null
  private readonly boundLoad = (event: Event) => this.onFrameLoad(event)
  private readonly boundError = (event: Event) => this.onFetchError(event)
  // Back/forward (advance history) restores the frame's content but the
  // persistent tab-bar is restored from a snapshot that captured the optimistic
  // active state, leaving it stale. turbo:load fires AFTER the restoration
  // renders, so reconcile the active tab from the URL there.
  private readonly boundTurboLoad = () => this.syncActiveFromUrl()

  connect(): void {
    document.addEventListener("turbo:frame-load", this.boundLoad)
    document.addEventListener("turbo:fetch-request-error", this.boundError)
    document.addEventListener("turbo:load", this.boundTurboLoad)
  }

  disconnect(): void {
    document.removeEventListener("turbo:frame-load", this.boundLoad)
    document.removeEventListener("turbo:fetch-request-error", this.boundError)
    document.removeEventListener("turbo:load", this.boundTurboLoad)
  }

  // Tab click: optimistically reflect the selection and spin the clicked tab.
  // Re-clicking the active tab is a no-op (avoids a pointless refetch).
  switch(event: Event): void {
    const tab = event.currentTarget as HTMLElement
    if (tab.getAttribute("aria-selected") === "true") {
      event.preventDefault()
      return
    }
    this.setActive(tab)
    this.setLoading(tab)
  }

  private isOurFrame(target: EventTarget | null): boolean {
    return target instanceof HTMLElement && target.id === FRAME_ID
  }

  private onFrameLoad(event: Event): void {
    if (!this.isOurFrame(event.target)) return
    this.clearLoading()
    // Reconcile with the URL (covers back/forward and skill-only swaps).
    this.syncActiveFromUrl()
  }

  private onFetchError(event: Event): void {
    if (!this.isOurFrame(event.target)) return
    this.clearLoading()
  }

  private setActive(active: HTMLElement): void {
    for (const tab of this.tabTargets) {
      tab.setAttribute("aria-selected", String(tab === active))
    }
  }

  private setLoading(tab: HTMLElement): void {
    this.clearLoading()
    tab.classList.add("ct-tab--loading")
    this.loadingTab = tab
  }

  private clearLoading(): void {
    this.loadingTab?.classList.remove("ct-tab--loading")
    this.loadingTab = null
  }

  private syncActiveFromUrl(): void {
    const type = new URL(window.location.href).searchParams.get("type") || DEFAULT_KEY
    const match = this.tabTargets.find((tab) => tab.dataset.key === type)
    if (match) this.setActive(match)
  }
}
